<template>
  <div class="space-y-6">
    <!-- Mõõdud -->
    <fieldset>
      <legend :class="legendClass">Mõõdud</legend>
      <div
        class="mt-3 grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-end gap-x-2"
      >
        <div>
          <label for="velg-laius" :class="labelClass">Laius (J)</label>
          <input
            id="velg-laius"
            v-model.trim="laius"
            type="number"
            :class="[inputClass, 'no-spin text-center']"
            placeholder="7.5"
            min="0"
            step="any"
            inputmode="decimal"
          />
        </div>
        <span class="pb-2 text-lg text-slate-300" aria-hidden="true">×</span>
        <div>
          <label for="velg-diameeter" :class="labelClass">Diameeter (")</label>
          <input
            id="velg-diameeter"
            v-model.trim="diameeter"
            type="number"
            :class="[inputClass, 'no-spin text-center']"
            placeholder="17"
            min="0"
            step="any"
            inputmode="decimal"
          />
        </div>
      </div>
    </fieldset>

    <!-- Kinnitus -->
    <fieldset>
      <legend :class="legendClass">Kinnitus</legend>
      <div class="mt-3 grid gap-3" :class="{ 'sm:grid-cols-2': !compact }">
        <div>
          <label for="velg-kinnitus" :class="labelClass">Puurimus (PCD)</label>
          <input
            id="velg-kinnitus"
            v-model.trim="kinnitus"
            type="text"
            :class="inputClass"
            placeholder="Nt. 5x112"
            aria-describedby="velg-kinnitus-hint"
          />
          <p
            v-if="boltPatternInvalid"
            id="velg-kinnitus-hint"
            class="mt-1 text-xs text-amber-700"
          >
            Kasuta kuju 5x112, muidu seda filtrit ei arvestata.
          </p>
        </div>
        <div>
          <label for="velg-tsentriava" :class="labelClass"
            >Tsentriava (CB, mm)</label
          >
          <input
            id="velg-tsentriava"
            v-model.trim="tsentriava"
            type="number"
            :class="[inputClass, 'no-spin']"
            placeholder="66.6"
            min="0"
            step="any"
            inputmode="decimal"
            aria-describedby="velg-tsentriava-hint"
          />
          <p id="velg-tsentriava-hint" class="mt-1 text-xs text-slate-500">
            Näitab ka suurema avaga velgi (tsentreerimisrõngaga).
          </p>
        </div>
      </div>
      <div
        class="mt-3 grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-end gap-x-2"
      >
        <div>
          <label for="velg-et-min" :class="labelClass">ET (min)</label>
          <input
            id="velg-et-min"
            v-model.trim="etMin"
            type="number"
            :class="[inputClass, 'no-spin text-center']"
            placeholder="35"
            step="any"
          />
        </div>
        <span class="pb-2 text-lg text-slate-300" aria-hidden="true">–</span>
        <div>
          <label for="velg-et-max" :class="labelClass">ET (max)</label>
          <input
            id="velg-et-max"
            v-model.trim="etMax"
            type="number"
            :class="[inputClass, 'no-spin text-center']"
            placeholder="50"
            step="any"
          />
        </div>
      </div>
    </fieldset>

    <!-- Tootja ja mudel -->
    <fieldset>
      <legend :class="legendClass">Tootja ja mudel</legend>
      <div class="mt-3 grid gap-3" :class="{ 'sm:grid-cols-2': !compact }">
        <div>
          <label for="velg-tootja" :class="labelClass">Tootja</label>
          <select id="velg-tootja" v-model="tootja" :class="selectClass">
            <option
              v-for="option in brandOptions"
              :key="option.value || option.label"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </div>
        <div>
          <label for="velg-mudel" :class="labelClass">Mudel</label>
          <input
            id="velg-mudel"
            v-model="mudel"
            type="text"
            :class="inputClass"
            placeholder="Nt. Union"
          />
        </div>
      </div>
    </fieldset>

    <!-- Velje tüüp -->
    <fieldset>
      <legend :class="legendClass">Velje tüüp</legend>
      <div
        class="mt-3 grid grid-cols-2 gap-2"
        :class="{ 'sm:grid-cols-3': !compact }"
      >
        <label
          v-for="group in wheelGroups"
          :key="group.id"
          :class="chipClass(tüübid.includes(group.id))"
        >
          <input
            v-model="tüübid"
            type="checkbox"
            :value="group.id"
            :class="checkboxClass"
          />
          {{ group.label }}
        </label>
      </div>
    </fieldset>
  </div>
</template>

<script>
import { WHEEL_GROUPS } from "@/config/catalog";
import { parseBoltPattern } from "@/catalog/wheelFilters";
import { chipClass, fieldModels, formClasses } from "./formShared";

export default {
  name: "WheelFilterForm",
  props: {
    modelValue: { type: Object, required: true },
    brandOptions: { type: Array, default: () => [] },
    // Narrow sidebar layout once results are shown.
    compact: { type: Boolean, default: false },
  },
  emits: ["update:modelValue"],
  data() {
    return { ...formClasses, wheelGroups: WHEEL_GROUPS };
  },
  computed: {
    ...fieldModels([
      "tootja",
      "mudel",
      "laius",
      "diameeter",
      "kinnitus",
      "etMin",
      "etMax",
      "tsentriava",
      "tüübid",
    ]),
    boltPatternInvalid() {
      return Boolean(this.kinnitus) && !parseBoltPattern(this.kinnitus);
    },
  },
  methods: {
    chipClass,
  },
};
</script>

<style scoped>
/* Hide number-input spinners so the compact size fields stay clean. */
.no-spin {
  -moz-appearance: textfield;
  appearance: textfield;
}

.no-spin::-webkit-outer-spin-button,
.no-spin::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
</style>
